import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { initializeAuth } from "./redux/actions/authActions";

// Auth related components
import Login from "./components/auth/Login/Login";
import ForgotPassword from "./components/auth/ForgotPassword/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword/ResetPassword";
import AuthLayout from "./components/auth/AuthLayout";

// Global components for error handling and loading
import GlobalError from "./components/common/GlobalError";
import GlobalLoader from "./components/common/GlobalLoader";

// Global components for printing
import PublicCarePlan from "./components/dashboard/CarePlan/PublicCarePlan";

// Admin dashboard components
import PrivateRoute from "./components/auth/Route/PrivateRoute";
import Layout from "./components/dashboard/Layout/Layout";
import Overview from "./components/dashboard/Admin/Overview";
import DraftCarePlans from "./components/dashboard/CarePlan/DraftCarePlans";
import ActiveCarePlans from "./components/dashboard/CarePlan/ActiveCarePlans";
import CarePlanBuilder from "./components/dashboard/CarePlanBuilder/CarePlanBuilder";
import MDTUsers from "./components/dashboard/MDTUser/MDTUsers";
import PrintDraftCarePlan from "./components/dashboard/CarePlan/PrintDraftCarePlan";
import CarePlanViewPage from "./components/dashboard/CarePlan/CarePlanViewPage";
import AuditLogsPage from "./components/dashboard/AuditLog/AuditLogsPage";
import UserGuide from "./components/dashboard/UserGuide/UserGuide";

// MDT user dashboard components
// import MDTUserLayout from "./components/dashboard/MDTUser/Layout/MDTUserLayout";
// import MDTOverview from "./components/dashboard/MDTUser/Overview/MDTOverview";
// import MDTDraftCarePlans from "./components/dashboard/MDTUser/DraftCarePlans/MDTDraftCarePlans";
// import MDTActiveCarePlans from "./components/dashboard/MDTUser/ActiveCarePlans/MDTActiveCarePlans";
// import MDTUserGuide from "./components/dashboard/MDTUser/UserGuide/MDTUserGuide";

const App = () => {
  const dispatch = useDispatch();
  const initialized = useSelector((state) => state.auth.initialized);

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  if (!initialized) {
    // Or some loading spinner while we verify token or refresh
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <GlobalLoader />
      <GlobalError />
      <Routes>
        {/* Public routes */}
        <Route element={<AuthLayout />}>
          {/* when you hit "/" you’ll stay inside AuthLayout and instantly swap to Login */}
          <Route index element={<Navigate to="login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot_password" element={<ForgotPassword />} />
          <Route
            path="/reset_password/:uid/:token"
            element={<ResetPassword />}
          />
        </Route>

        <Route
          path="share/:publicId"
          element={<PublicCarePlan mode="digital" />}
        />
        <Route
          path="share/:publicId/print"
          element={<PublicCarePlan mode="print" />}
        />
        <Route path="draft-print/:publicId" element={<PrintDraftCarePlan />} />

        {/* Admin dashboard routes */}
        <Route
          path="/admin_dashboard/*"
          element={
            <PrivateRoute adminOnly={true}>
              <Routes>
                <Route
                  path="overview"
                  element={
                    <Layout title="Overview">
                      <Overview />
                    </Layout>
                  }
                />
                <Route
                  path="draft-care-plans"
                  element={
                    <Layout title="Draft Care Plans">
                      <DraftCarePlans />
                    </Layout>
                  }
                />
                <Route
                  path="active-care-plans"
                  element={
                    <Layout title="Active Care Plans">
                      <ActiveCarePlans />
                    </Layout>
                  }
                />
                <Route
                  path="care-plan-builder"
                  element={
                    <Layout title="Care Plan Builder">
                      <CarePlanBuilder />
                    </Layout>
                  }
                />
                <Route
                  path="mdt-users"
                  element={
                    <Layout title="Users">
                      <MDTUsers />
                    </Layout>
                  }
                />
                <Route
                  path="user-guide"
                  element={
                    <Layout title="User guide">
                      <UserGuide />
                    </Layout>
                  }
                />
                <Route
                  path="audit-log"
                  element={
                    <Layout title="Audit Log">
                      <AuditLogsPage />
                    </Layout>
                  }
                />
                <Route
                  path="care-plans/:publicId"
                  element={<CarePlanViewPage />}
                />

                {/* Not yet agreed for developmet by the client */}
                {/* <Route
                  path="reports"
                  element={
                    <Layout title="Reports">
                      <Overview />
                    </Layout>
                  }
                /> */}
              </Routes>
            </PrivateRoute>
          }
        />

        {/* MDT-User routes */}
        <Route
          path="/mdt_dashboard/*"
          element={
            <PrivateRoute adminOnly={false}>
              <Routes>
                <Route
                  path="overview"
                  element={
                    <Layout title="Overview">
                      <Overview />
                    </Layout>
                  }
                />
                <Route
                  path="draft-care-plans"
                  element={
                    <Layout title="Draft Care Plans">
                      <DraftCarePlans />
                    </Layout>
                  }
                />
                <Route
                  path="active-care-plans"
                  element={
                    <Layout title="Active Care Plans">
                      <ActiveCarePlans />
                    </Layout>
                  }
                />
                <Route
                  path="user-guide"
                  element={
                    <Layout title="User Guide">
                      <UserGuide />
                    </Layout>
                  }
                />
                <Route
                  path="care-plans/:publicId"
                  element={<CarePlanViewPage />}
                />
              </Routes>
            </PrivateRoute>
          }
        />

        {/* <Route
          path="/user_dashboard"
          element={
            <PrivateRoute>
              <Layout>
                <MDTUserDashboard />
              </Layout>
            </PrivateRoute>
          }
        /> */}
        {/* Fallback to /login if no route matches */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </Router>
  );
};

export default App;
